import { CardContent } from "@/components/CardContent";
import { HeaderPage } from "@/components/HeaderPage";
import { Loading } from "@/components/Loading";
import { Table } from "@/components/Table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent as CardContentUI } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import type { Team } from "@/types/Team";
import { Eye, Plus, Search, Trash2 } from "lucide-react";
import { useState } from "react";
import { MdOutlineEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useGetTeams, useDeleteTeam } from "@/hooks/useTeams";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import toast from "react-hot-toast";
import { useDebounce } from "@/hooks/useDebounce";

const workShiftLabels: Record<string, string> = {
  MORNING: "Manhã",
  AFTERNOON: "Tarde",
  NIGHT: "Noite",
  FULL_TIME: "Integral",
};

export const Teams = () => {
  const navigate = useNavigate();
  const deleteTeam = useDeleteTeam();
  const [searchInput, setSearchInput] = useState("");
  const [status, setStatus] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [teamToDelete, setTeamToDelete] = useState<string | null>(null);
  const [limit] = useState(10); // Items per page

  const debouncedSearch = useDebounce(searchInput, 500);

  const { data: teamsData, isLoading } = useGetTeams({
    search: debouncedSearch,
    status: status,
    page: currentPage,
    limit: limit,
  });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleStatusChange = (value: string) => {
    setStatus(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async () => {
    if (!teamToDelete) return;

    try {
      await deleteTeam.mutateAsync(teamToDelete);
      toast.success("Equipe deletada com sucesso!");
      setTeamToDelete(null);
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.message || "Erro ao deletar equipe";
      toast.error(errorMessage);
    }
  };

  return (
    <>
      <HeaderPage
        title="Equipes"
        description="Crie uma nova equipe para gerenciar seus membros e tarefas."
      />

      <Card className="mb-6 mt-6 bg-white">
        <CardContentUI className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Buscar equipes..."
                className="pl-10 h-10 text-sm"
                value={searchInput}
                onChange={handleSearchChange}
              />
            </div>
            <div className="flex gap-3">
              <Select value={status} onValueChange={handleStatusChange}>
                <SelectTrigger className="w-45 h-10 text-sm">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="ACTIVE">Ativos</SelectItem>
                  <SelectItem value="INACTIVE">Inativos</SelectItem>
                </SelectContent>
              </Select>
              <Button
                className="h-10 text-sm px-4 text-white cursor-pointer bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]"
                onClick={() => navigate("/teams/create")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Nova equipe
              </Button>
            </div>
          </div>
        </CardContentUI>
      </Card>

      <CardContent
        title="Lista de equipes"
        content={
          isLoading ? (
            <Loading label="Carregando equipes.." />
          ) : (
            <Table<Team>
              columns={[
                { key: "id", label: "Id" },
                { key: "name", label: "Nome" },
                { key: "regional", label: "Regional" },
                {
                  key: "workShift",
                  label: "Turno de trabalho",
                  render: (team) =>
                    workShiftLabels[team.workShift] || team.workShift,
                },
                {
                  key: "status",
                  label: "Status",
                  render: (team) => (
                    <Badge
                      variant={
                        team.status === "ACTIVE" ? "default" : "secondary"
                      }
                      className={
                        team.status === "ACTIVE"
                          ? "bg-green-500"
                          : "bg-gray-500"
                      }
                    >
                      {team.status === "ACTIVE" ? "Ativa" : "Inativa"}
                    </Badge>
                  ),
                },
                {
                  key: "actions",
                  label: "Ações",
                  render: (team) => {
                    return (
                      <div className="flex items-center">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                          title="Visualizar"
                          onClick={() => navigate(`/teams/view/${team.id}`)}
                        >
                          <Eye className="h-5 w-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer hover:bg-gray-100 transition-colors"
                          title="Editar"
                          onClick={() => navigate(`/teams/edit/${team.id}`)}
                        >
                          <MdOutlineEdit className="h-5 w-5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 cursor-pointer hover:bg-red-50 transition-colors"
                          title="Deletar"
                          onClick={() => setTeamToDelete(team.id)}
                        >
                          <Trash2 className="h-5 w-5 " />
                        </Button>
                      </div>
                    );
                  },
                },
              ]}
              data={teamsData?.data || []}
              pagination={teamsData?.pagination}
              onPageChange={handlePageChange}
            />
          )
        }
      />

      <ConfirmDialog
        open={!!teamToDelete}
        onOpenChange={() => setTeamToDelete(null)}
        onConfirm={handleDelete}
        title="Deletar Equipe"
        description="Tem certeza que deseja deletar esta equipe? Esta ação não pode ser desfeita."
        confirmText="Deletar"
        cancelText="Cancelar"
        variant="destructive"
        isLoading={deleteTeam.isPending}
      />
    </>
  );
};
